/**
 * Small display pieces for the calibrated-range rule (utils/maturityDisplay):
 * a tooltip wrapper for bounded / NCS* strengths, and the marker for targets
 * whose strength lies outside the calibration's tested range.
 */
import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import type { CalibratedRange, Target } from '../../services/pours';
import {
  OUTSIDE_RANGE_TOOLTIP,
  TARGET_OUTSIDE_RANGE_LABEL,
  isTargetOutsideRange,
} from '../../utils/maturityDisplay';

/** Wraps `children` in a tooltip when `title` is set (e.g. "Outside the
 *  tested strength-maturity range" on "< 3.3 MPa"). */
export const StrengthTip: React.FC<{ title?: string | null; children: React.ReactElement }> = ({
  title,
  children,
}) =>
  title ? (
    <Tooltip title={title} placement="top" arrow>
      {children}
    </Tooltip>
  ) : (
    children
  );

/** Dotted underline that hints a value has a tooltip. */
export const tipUnderline = (title?: string | null) =>
  title ? { textDecoration: 'underline dotted', textUnderlineOffset: '3px', cursor: 'help' } : {};

/** The targets flagged outside the tested range, in maturity order. */
export const outsideRangeTargets = (
  targets: Target[] | null | undefined,
  range?: CalibratedRange | null,
): Target[] =>
  [...(targets ?? [])]
    .filter((t) => isTargetOutsideRange(t, range))
    .sort((a, b) => (a.target_maturity ?? 0) - (b.target_maturity ?? 0));

const describeTarget = (t: Target, unit?: string) =>
  t.target_strength != null && t.target_strength > 0
    ? `${t.name} (${t.target_strength.toFixed(1)}${unit ? ` ${unit}` : ''})`
    : t.name;

interface OutsideRangeTargetsProps {
  targets: Target[] | null | undefined;
  range?: CalibratedRange | null;
  strengthUnit?: string;
  /** 'caption' (default): icon plus "Outside tested range: name (strength)".
   *  'icon': the icon alone, with the targets in its tooltip (tight rows). */
  variant?: 'caption' | 'icon';
}

/** Marks targets whose strength is outside the tested range. Renders
 *  nothing when none is flagged. */
export const OutsideRangeTargets: React.FC<OutsideRangeTargetsProps> = ({
  targets,
  range,
  strengthUnit,
  variant = 'caption',
}) => {
  const flagged = outsideRangeTargets(targets, range);
  if (flagged.length === 0) return null;
  const names = flagged.map((t) => describeTarget(t, strengthUnit)).join(', ');
  const tooltip = `${TARGET_OUTSIDE_RANGE_LABEL}: ${names}. ${OUTSIDE_RANGE_TOOLTIP}.`;

  if (variant === 'icon') {
    return (
      <Tooltip title={tooltip} placement="top" arrow>
        <WarningAmberIcon
          aria-label={`${TARGET_OUTSIDE_RANGE_LABEL}: ${names}`}
          sx={{ fontSize: 14, color: 'warning.main', verticalAlign: 'middle', ml: 0.5, cursor: 'help' }}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={OUTSIDE_RANGE_TOOLTIP} placement="bottom-start" arrow>
      <Box
        data-testid="outside-range-targets"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, minWidth: 0, cursor: 'help' }}
      >
        <WarningAmberIcon sx={{ fontSize: 13, color: 'warning.main', flexShrink: 0 }} />
        <Typography variant="caption" noWrap sx={{ fontSize: '0.68rem', color: 'warning.dark' }}>
          {TARGET_OUTSIDE_RANGE_LABEL}: {names}
        </Typography>
      </Box>
    </Tooltip>
  );
};
